import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createKnowledgeApi } from "../api";
import {
  allowedPolicyActions,
  formatLabel,
  policyStatusTone,
} from "../labels";
import type {
  PolicyAction,
  PolicyDto,
  PolicyVersionDto,
} from "../types";
import {
  ConfirmActionDialog,
  CreatePolicyVersionDialog,
} from "../components/KnowledgeDialogs";

export function PolicyDetailPage() {
  const { policyId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createKnowledgeApi(client), [client]);
  const toast = useToast();

  const [policy, setPolicy] = useState<PolicyDto | null>(null);
  const [versions, setVersions] = useState<PolicyVersionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionId, setVersionId] = useState("");
  const [versionActionOpen, setVersionActionOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Knowledge Base", to: "/app/knowledge" },
    { label: "Policies", to: "/app/knowledge/policies" },
    { label: policy?.policy_key ?? "Policy" },
  ]);

  const load = useCallback(async () => {
    if (!policyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getPolicy(policyId);
      setPolicy(result.policy);
      setVersions(result.versions);
      setVersionId(
        result.policy.current_version_id ?? result.versions[0]?.id ?? "",
      );
    } catch (err) {
      setError(apiErrorMessage(err));
      setPolicy(null);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [api, policyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading policy…" />;
  if (error || !policy) {
    return (
      <EmptyState
        title="Policy unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedPolicyActions(policy.status);
  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const versionActions = selectedVersion
    ? allowedPolicyActions(selectedVersion.status)
    : [];

  return (
    <Stack gap={24}>
      <SectionHeader
        title={policy.policy_key}
        description={`${formatLabel(policy.owning_department)} · policy`}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() => {
                setActionError(null);
                setVersionOpen(true);
              }}
            >
              Add version
            </Button>
            {actions.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Policy lifecycle
              </Button>
            ) : null}
          </Flex>
        }
      />

      <ActionBar
        left={
          <StatusChip tone={policyStatusTone(policy.status)}>
            {formatLabel(policy.status)}
          </StatusChip>
        }
      />

      <Alert tone="warning" title="Activate requires a version">
        activate needs version_id (defaults to current_version_id when set).
        Version rows are the revision/history surface for Policies.
      </Alert>

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Policy
          </Text>
          <Text role="body" as="p">
            ID: {policy.id}
          </Text>
          <Text role="body" as="p">
            Owner: {policy.owner_user_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Current version ID: {policy.current_version_id ?? "—"}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={12}>
          <Text role="h2" as="h2">
            Versions / history
          </Text>
          {versions.length === 0 ? (
            <EmptyState
              title="No versions"
              description="Add a version before activate."
              reason="none"
            />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Version</Th>
                  <Th>Status</Th>
                  <Th>Effective</Th>
                  <Th>Approved by</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {versions.map((version) => (
                  <Tr key={version.id}>
                    <Td>
                      {version.version}
                      {policy.current_version_id === version.id
                        ? " (current)"
                        : ""}
                    </Td>
                    <Td>
                      <StatusChip tone={policyStatusTone(version.status)}>
                        {formatLabel(version.status)}
                      </StatusChip>
                    </Td>
                    <Td>
                      {version.effective_from}
                      {version.effective_to ? ` → ${version.effective_to}` : ""}
                    </Td>
                    <Td>{version.approved_by_user_id ?? "—"}</Td>
                    <Td>
                      {allowedPolicyActions(version.status).length > 0 ? (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setSelectedVersionId(version.id);
                            setActionError(null);
                            setVersionActionOpen(true);
                          }}
                        >
                          Version actions
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
          {versions.length > 0 ? (
            <Text role="caption" color="muted" as="p">
              Content references:{" "}
              {versions
                .map((v) => `${v.version}=${v.content_reference}`)
                .join("; ")}
            </Text>
          ) : null}
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Policy lifecycle"
        notice="submit_review → approve → activate → supersede / retire. For activate, choose version_id below."
        actions={actions}
        extraFields={
          <Input
            label="Version ID (required for activate)"
            value={versionId}
            onChange={(event) => setVersionId(event.target.value)}
          />
        }
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const action = input.action as PolicyAction;
            const body: Record<string, unknown> = {};
            if (action === "activate") {
              if (!versionId.trim()) {
                setActionError("version_id is required to activate");
                setBusy(false);
                return;
              }
              body.version_id = versionId.trim();
            }
            await api.applyPolicyAction(policy.id, action, body);
            toast.push({
              title: `Policy ${formatLabel(action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreatePolicyVersionDialog
        open={versionOpen}
        onClose={() => setVersionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createPolicyVersion(policy.id, body);
            toast.push({ title: "Version created", tone: "success" });
            setVersionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmActionDialog
        open={versionActionOpen && selectedVersionId != null}
        title="Policy version lifecycle"
        notice={`Actions on version ${selectedVersion?.version ?? selectedVersionId}. Same lifecycle enum as Policy.`}
        actions={versionActions}
        onClose={() => {
          setVersionActionOpen(false);
          setSelectedVersionId(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!selectedVersionId) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyPolicyVersionAction(
              policy.id,
              selectedVersionId,
              input.action as PolicyAction,
            );
            toast.push({
              title: `Version ${formatLabel(input.action)}`,
              tone: "success",
            });
            setVersionActionOpen(false);
            setSelectedVersionId(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
